import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardsApi } from '../api/dashboards';
import { leadsApi, Lead } from '../api/leads';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import { Link } from 'react-router-dom';
import { parseLeadValue, parseLeadProbability } from '../utils/leadUtils';
import { formatCurrency, Currency } from '../utils/currencyUtils';
import CloseSummary from '../components/CloseSummary';
import Tooltip from '../components/Tooltip';
import {
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
  PresentationChartLineIcon,
  PlusIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingOfficeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

// Stage definitions with icons, colors and tooltips
const STAGES = [
  { id: 'new', name: 'New', icon: '🆕', color: 'bg-slate-500', tooltip: 'Fresh leads not yet contacted' },
  { id: 'contacted', name: 'Contacted', icon: '📞', color: 'bg-blue-500', tooltip: 'Initial contact made via call/email' },
  { id: 'first_meeting', name: 'First Meeting', icon: '🤝', color: 'bg-cyan-500', tooltip: 'First discovery meeting completed' },
  { id: 'second_meeting', name: 'Second Meeting', icon: '🤝', color: 'bg-teal-500', tooltip: 'Follow-up meeting completed' },
  { id: 'qualified', name: 'Qualified', icon: '✅', color: 'bg-emerald-500', tooltip: 'Lead qualified - set A/B/C priority' },
  { id: 'pitch_presented', name: 'Pitch Presented', icon: '📊', color: 'bg-amber-500', tooltip: 'Solution pitch delivered' },
  { id: 'sow_in_progress', name: 'SOW in Progress', icon: '📝', color: 'bg-orange-500', tooltip: 'Preparing Statement of Work' },
  { id: 'sow_sent', name: 'SOW Sent', icon: '📨', color: 'bg-pink-500', tooltip: 'Proposal sent to client' },
  { id: 'clarification_call', name: 'Clarification', icon: '❓', color: 'bg-fuchsia-500', tooltip: 'Answering client questions' },
  { id: 'roi_proof_call', name: 'ROI/Proof', icon: '📈', color: 'bg-purple-500', tooltip: 'ROI discussion completed' },
  { id: 'decision_stage', name: 'Decision', icon: '⚖️', color: 'bg-violet-500', tooltip: 'Client making final decision' },
  { id: 'negotiation', name: 'Negotiation', icon: '💬', color: 'bg-indigo-500', tooltip: 'Negotiating terms & pricing' },
  { id: 'won', name: 'Won', icon: '🏆', color: 'bg-green-500', tooltip: 'Deal closed successfully!' },
  { id: 'lost', name: 'Lost', icon: '❌', color: 'bg-red-500', tooltip: 'Deal lost - record reason' },
];

const Dashboard: React.FC = () => {
  // Helper: contact name from lead (uses primary contact from API when available)
  const getContactName = (lead: Lead): string => {
    const first = lead.primary_contact_first_name ?? lead.first_name;
    const last = lead.primary_contact_last_name ?? lead.last_name;
    if (first && last) return `${first} ${last}`.trim();
    if (first) return first;
    return lead.lead_name || '';
  };
  const { user } = useAuth();
  const isLeadership = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'leadership';
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [period, setPeriod] = useState<'weekly' | 'monthly' | 'quarterly' | 'all'>('all');
  const [insightsModal, setInsightsModal] = useState<{ type: 'pipeline' | 'forecast' | 'won' } | null>(null);
  
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
    enabled: !!user,
    staleTime: 0,
  });

  // Filter leads by period
  const getFilteredLeadsByPeriod = (leadList: typeof leads) => {
    // If "all" is selected, return all leads without filtering
    if (period === 'all') {
      return leadList;
    }
    
    const now = new Date();
    // Set to end of today for proper comparison
    now.setHours(23, 59, 59, 999);
    
    let startDate: Date;
    
    switch (period) {
      case 'weekly':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0); // Start of day
        break;
      case 'monthly':
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0); // Start of day
        break;
      case 'quarterly':
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 3);
        startDate.setHours(0, 0, 0, 0); // Start of day
        break;
      default:
        return leadList; // Default to all if unknown period
    }
    
    const filtered = leadList.filter(lead => {
      try {
        // Get the relevant date for filtering based on lead stage
        let relevantDate: Date | null = null;
        
        if (lead.stage === 'won') {
          // For won leads, use won_date (date when it was won)
          const dateStr = lead.won_date || lead.updated_at || lead.date_created || lead.created_at;
          if (dateStr) {
            relevantDate = new Date(dateStr);
          }
        } else if (lead.stage === 'lost') {
          // For lost leads, use lost_date (date when it was lost)
          const dateStr = lead.lost_date || lead.updated_at || lead.date_created || lead.created_at;
          if (dateStr) {
            relevantDate = new Date(dateStr);
          }
        } else {
          // For active leads, prioritize: created date, then updated date
          const dateStr = lead.date_created || lead.created_at || lead.updated_at;
          if (dateStr) {
            relevantDate = new Date(dateStr);
          }
        }
        
        // If no date found, exclude the lead
        if (!relevantDate || isNaN(relevantDate.getTime())) {
          return false;
        }
        
        // Check if the relevant date is within the period
        const dateTime = relevantDate.getTime();
        const startTime = startDate.getTime();
        const endTime = now.getTime();
        
        return dateTime >= startTime && dateTime <= endTime;
      } catch (error) {
        // If date parsing fails, exclude the lead
        return false;
      }
    });
    
    return filtered;
  };

  // Get period-filtered leads for all dashboard calculations
  const periodFilteredLeads = getFilteredLeadsByPeriod(leads);
  
  // Calculate all metrics based on period-filtered leads
  const getStageCount = (stageId: string) => periodFilteredLeads.filter(l => l.stage === stageId).length;
  const getStageValue = (stageId: string) => periodFilteredLeads.filter(l => l.stage === stageId).reduce((sum, l) => {
    return sum + parseLeadValue(l.value);
  }, 0);

  const totalLeads = periodFilteredLeads.length;
  const activeLeads = periodFilteredLeads.filter(l => !['won', 'lost'].includes(l.stage)).length;
  const wonLeads = getStageCount('won');
  const lostLeads = getStageCount('lost');
  const wonValue = getStageValue('won');
  const lostValue = getStageValue('lost');
  const totalPipelineValue = periodFilteredLeads.filter(l => !['won', 'lost'].includes(l.stage)).reduce((sum, l) => {
    return sum + parseLeadValue(l.value);
  }, 0);
  const weightedForecast = periodFilteredLeads.filter(l => !['won', 'lost'].includes(l.stage)).reduce((sum, l) => {
    const value = parseLeadValue(l.value);
    const prob = parseLeadProbability(l.probability);
    return sum + (value * prob / 100);
  }, 0);

  // Win rate calculation
  const closedDeals = wonLeads + lostLeads;
  const winRate = closedDeals > 0 ? Math.round((wonLeads / closedDeals) * 100) : 0;

  // Category breakdown (A/B/C) - get leads and counts from period-filtered leads
  // Exclude lost leads from category breakdown - they will be shown separately
  const activeLeadsForCategory = periodFilteredLeads.filter(l => l.stage !== 'lost');
  const categoryALeads = activeLeadsForCategory.filter(l => l.category === 'A');
  const categoryBLeads = activeLeadsForCategory.filter(l => l.category === 'B');
  const categoryCLeads = activeLeadsForCategory.filter(l => l.category === 'C');
  const uncategorizedLeads = activeLeadsForCategory.filter(l => !l.category || !['A', 'B', 'C'].includes(l.category));
  const categoryA = categoryALeads.length;
  const categoryB = categoryBLeads.length;
  const categoryC = categoryCLeads.length;
  
  // Lost leads - separate category (from period-filtered leads)
  const lostLeadsForCategory = periodFilteredLeads.filter(l => l.stage === 'lost');

  // Service Line breakdown - group period-filtered leads by service_line
  const serviceLineGroups = periodFilteredLeads.reduce((acc, lead) => {
    const serviceLine = lead.service_line || 'Not Specified';
    if (!acc[serviceLine]) {
      acc[serviceLine] = [];
    }
    acc[serviceLine].push(lead);
    return acc;
  }, {} as Record<string, typeof leads>);
  
  const serviceLineEntries = Object.entries(serviceLineGroups).sort((a, b) => b[1].length - a[1].length); // Sort by count descending
  
  // Performance metric leads (from period-filtered leads)
  const newLeads = periodFilteredLeads.filter(l => l.stage === 'new');
  // Not Scheduled: Leads that are in "contacted" stage but no meeting has been scheduled yet
  // These are leads that have been contacted but haven't moved to first_meeting or second_meeting
  const notContactedLeads = periodFilteredLeads.filter(l => {
    const stage = l.stage;
    // Show leads that are in "contacted" stage but NOT in meeting stages
    // This means they were contacted but no meeting has been scheduled
    return stage === 'contacted';
  });
  const meetingLeads = periodFilteredLeads.filter(l => ['first_meeting', 'second_meeting'].includes(l.stage));
  const sowLeads = periodFilteredLeads.filter(l => ['sow_in_progress', 'sow_sent'].includes(l.stage));
  const negotiationLeads = periodFilteredLeads.filter(l => l.stage === 'negotiation');
  const decisionLeads = periodFilteredLeads.filter(l => l.stage === 'decision_stage');

  if (leadsLoading) {
    return (
      <PremiumLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title={isLeadership ? 'Leadership Dashboard' : 'My Dashboard'}
      subtitle={`Welcome back, ${user?.name}. ${period === 'all' ? 'Showing all-time data.' : `Showing data for ${period === 'weekly' ? 'this week' : period === 'monthly' ? 'this month' : 'this quarter'}.`}`}
      actions={
        <div className="flex items-center space-x-3">
          <div className="relative">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as 'weekly' | 'monthly' | 'quarterly' | 'all')}
              className="px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium cursor-pointer appearance-none"
              style={{ pointerEvents: 'auto', zIndex: 10 }}
            >
              <option value="all">All Time</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>
          <Link to="/leads" className="btn-primary text-sm px-4 py-2">
            <PlusIcon className="h-4 w-4 mr-2" />
            New Lead
          </Link>
        </div>
      }
    >
      <div className="space-y-6">
        
        {/* Top Summary Cards - Key Numbers with Tooltips */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Tooltip content="Total number of leads in your pipeline (all stages)" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help w-full">
              <div className="text-3xl font-bold text-gray-900">{totalLeads}</div>
              <div className="text-sm text-gray-500 flex items-center">
                Total Leads
                <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </div>
            </div>
          </Tooltip>
          <Tooltip content="Leads currently being worked on (excludes Won & Lost)" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help w-full">
              <div className="text-3xl font-bold text-blue-600">{activeLeads}</div>
              <div className="text-sm text-gray-500 flex items-center">
                Active Pipeline
                <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </div>
            </div>
          </Tooltip>
          <Tooltip content="Deals that have been successfully closed" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help w-full">
              <div className="text-3xl font-bold text-green-600">{wonLeads}</div>
              <div className="text-sm text-gray-500 flex items-center">
                Won 🎉
                <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </div>
            </div>
          </Tooltip>
          <Tooltip content="Deals that did not convert - review reasons to improve" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help w-full">
              <div className="text-3xl font-bold text-red-600">{lostLeads}</div>
              <div className="text-sm text-gray-500 flex items-center">
                Lost
                <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </div>
            </div>
          </Tooltip>
          <Tooltip content="Won ÷ (Won + Lost) × 100. Higher is better!" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help w-full">
              <div className="text-3xl font-bold text-purple-600">{winRate}%</div>
              <div className="text-sm text-gray-500 flex items-center">
                Win Rate
                <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </div>
            </div>
          </Tooltip>
          <Tooltip content="Total value of all deals you've won" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help w-full">
              <div className="text-xl font-bold text-indigo-600">{formatCurrency(wonValue, 'INR', { compact: true })}</div>
              <div className="text-sm text-gray-500 flex items-center">
                Won Value
                <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </div>
            </div>
          </Tooltip>
        </div>

        {/* Value Summary Cards with Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Pipeline Value */}
          <button
            onClick={() => setInsightsModal({ type: 'pipeline' })}
            className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-6 text-white hover:shadow-lg transition-all duration-200 w-full text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-sm opacity-80 flex items-center mb-1">
                  Pipeline Value
                  <InformationCircleIcon className="h-4 w-4 ml-1 opacity-60" />
                </div>
                <div className="text-3xl font-bold mt-1">{formatCurrency(totalPipelineValue, 'INR', { compact: true })}</div>
                <div className="text-sm opacity-80 mt-1">{activeLeads} active deals</div>
                <div className="mt-3 pt-2 border-t border-white/20">
                  <span className="text-xs font-medium opacity-90 hover:opacity-100 flex items-center">
                    View Insights →
                  </span>
                </div>
              </div>
              <CurrencyDollarIcon className="h-12 w-12 opacity-50 flex-shrink-0 ml-4" />
            </div>
          </button>

          {/* Weighted Forecast */}
          <button
            onClick={() => setInsightsModal({ type: 'forecast' })}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-6 text-white hover:shadow-lg transition-all duration-200 w-full text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-sm opacity-80 flex items-center mb-1">
                  Weighted Forecast
                  <InformationCircleIcon className="h-4 w-4 ml-1 opacity-60" />
                </div>
                <div className="text-3xl font-bold mt-1">{formatCurrency(weightedForecast, 'INR', { compact: true })}</div>
                <div className="text-sm opacity-80 mt-1">Probability adjusted</div>
                <div className="mt-3 pt-2 border-t border-white/20">
                  <span className="text-xs font-medium opacity-90 hover:opacity-100 flex items-center">
                    View Insights →
                  </span>
                </div>
              </div>
              <ArrowTrendingUpIcon className="h-12 w-12 opacity-50 flex-shrink-0 ml-4" />
            </div>
          </button>

          {/* Total Won Value */}
          <button
            onClick={() => setInsightsModal({ type: 'won' })}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-6 text-white hover:shadow-lg transition-all duration-200 w-full text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-sm opacity-80 flex items-center mb-1">
                  Total Won Value
                  <InformationCircleIcon className="h-4 w-4 ml-1 opacity-60" />
                </div>
                <div className="text-3xl font-bold mt-1">{formatCurrency(wonValue, 'INR', { compact: true })}</div>
                <div className="text-sm opacity-80 mt-1">{wonLeads} deals closed</div>
                <div className="mt-3 pt-2 border-t border-white/20">
                  <span className="text-xs font-medium opacity-90 hover:opacity-100 flex items-center">
                    View Insights →
                  </span>
                </div>
              </div>
              <CheckCircleIcon className="h-12 w-12 opacity-50 flex-shrink-0 ml-4" />
            </div>
          </button>
        </div>

        {/* AI Summary */}
        <CloseSummary currency="INR" />

        {/* Stage-by-Stage Checklist - The Main View */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">📋 Pipeline Checklist - All Stages at a Glance</h2>
            <p className="text-sm text-gray-500">Click any stage to view its leads. Hover for description.</p>
          </div>
          <div className="p-4">
            {/* Active Pipeline Stages */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {STAGES.filter(stage => stage.id !== 'won' && stage.id !== 'lost').map((stage) => {
                const count = getStageCount(stage.id);
                const value = getStageValue(stage.id);
                const hasLeads = count > 0;
                
                return (
                  <Tooltip key={stage.id} content={stage.tooltip} position="top">
                    <Link
                      to={`/leads?stage=${stage.id}`}
                      className={`flex items-center p-3 rounded-lg border-2 transition-all hover:shadow-md w-full ${
                        hasLeads 
                          ? 'border-gray-200 bg-white hover:border-gray-300' 
                          : 'border-dashed border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg ${stage.color} flex items-center justify-center text-white text-lg mr-3 flex-shrink-0`}>
                        {stage.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate flex items-center">
                          {stage.name}
                          <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400 flex-shrink-0" />
                    </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`font-bold ${hasLeads ? 'text-gray-900' : 'text-gray-400'}`}>
                            {count} leads
                          </span>
                          {hasLeads && (
                            <span className="text-gray-400">
                              • {formatCurrency(value, 'INR', { compact: true })}
                            </span>
                          )}
                    </div>
                  </div>
                      {hasLeads && (
                        <div className="ml-2 bg-gray-100 rounded-full px-2.5 py-1 text-xs font-bold text-gray-700 flex-shrink-0">
                          {count}
                        </div>
                      )}
                    </Link>
                  </Tooltip>
                );
              })}
            </div>

            {/* Separator */}
            <div className="my-6 border-t-2 border-gray-200"></div>

            {/* Final Outcomes - Won & Lost as Separate Categories */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Final Outcomes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Won */}
                {STAGES.filter(stage => stage.id === 'won').map((stage) => {
                  const count = getStageCount(stage.id);
                  const value = getStageValue(stage.id);
                  const hasLeads = count > 0;
                  
                  return (
                    <Tooltip key={stage.id} content={stage.tooltip} position="top">
                      <Link
                        to={`/leads?stage=${stage.id}`}
                        className={`flex items-center p-4 rounded-lg border-2 transition-all hover:shadow-md w-full ${
                          hasLeads 
                            ? 'border-green-300 bg-green-50 hover:border-green-400 hover:bg-green-100' 
                            : 'border-dashed border-green-200 bg-green-50 opacity-60'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg ${stage.color} flex items-center justify-center text-white text-xl mr-4 flex-shrink-0`}>
                          {stage.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-900 truncate flex items-center text-lg">
                            {stage.name}
                            <InformationCircleIcon className="h-4 w-4 ml-2 text-gray-400 flex-shrink-0" />
                          </div>
                          <div className="flex items-center gap-2 text-sm mt-1">
                            <span className={`font-bold ${hasLeads ? 'text-gray-900' : 'text-gray-400'}`}>
                              {count} deals
                            </span>
                            {hasLeads && (
                              <span className="text-gray-500">
                                • {formatCurrency(value, 'INR', { compact: true })}
                              </span>
                            )}
                          </div>
                        </div>
                        {hasLeads && (
                          <div className="ml-2 bg-green-200 rounded-full px-3 py-1.5 text-sm font-bold text-green-800 flex-shrink-0">
                            {count}
                          </div>
                        )}
                      </Link>
                    </Tooltip>
                  );
                })}

                {/* Lost - Separate Category */}
                {STAGES.filter(stage => stage.id === 'lost').map((stage) => {
                  const count = getStageCount(stage.id);
                  const value = getStageValue(stage.id);
                  const hasLeads = count > 0;
                  
                  return (
                    <Tooltip key={stage.id} content={stage.tooltip} position="top">
                      <Link
                        to={`/leads?stage=${stage.id}`}
                        className={`flex items-center p-4 rounded-lg border-2 transition-all hover:shadow-md w-full ${
                          hasLeads 
                            ? 'border-red-300 bg-red-50 hover:border-red-400 hover:bg-red-100' 
                            : 'border-dashed border-red-200 bg-red-50 opacity-60'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg ${stage.color} flex items-center justify-center text-white text-xl mr-4 flex-shrink-0`}>
                          {stage.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-900 truncate flex items-center text-lg">
                            {stage.name}
                            <InformationCircleIcon className="h-4 w-4 ml-2 text-gray-400 flex-shrink-0" />
                          </div>
                          <div className="flex items-center gap-2 text-sm mt-1">
                            <span className={`font-bold ${hasLeads ? 'text-gray-900' : 'text-gray-400'}`}>
                              {count} deals
                            </span>
                            {hasLeads && (
                              <span className="text-gray-500">
                                • {formatCurrency(value, 'INR', { compact: true })}
                              </span>
                            )}
                          </div>
                        </div>
                        {hasLeads && (
                          <div className="ml-2 bg-red-200 rounded-full px-3 py-1.5 text-sm font-bold text-red-800 flex-shrink-0">
                            {count}
                          </div>
                        )}
                      </Link>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Help text */}
          <div className="px-6 py-3 bg-blue-50 border-t border-blue-100">
            <p className="text-xs text-blue-700">
              💡 <strong>Tip:</strong> Move leads through stages as you progress. Category A = Hot, B = Warm, C = Nurture.
            </p>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Category Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Category Breakdown (A/B/C)</h3>
            <div className="space-y-3">
              {/* Category A */}
              <div className="bg-emerald-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('categoryA')}
                  className="w-full flex items-center justify-between p-3 hover:bg-emerald-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-emerald-500 text-white rounded-lg flex items-center justify-center font-bold mr-3">A</span>
                    <span className="font-medium text-gray-900">High Priority</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-emerald-600">{categoryA}</span>
                    {categoryA > 0 && (
                      expandedSections.categoryA ? (
                        <ChevronUpIcon className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-emerald-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.categoryA && categoryALeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {categoryALeads.map((lead) => (
              <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-emerald-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                </div>
                        <span className="text-xs font-medium text-emerald-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
              </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Category B */}
              <div className="bg-amber-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('categoryB')}
                  className="w-full flex items-center justify-between p-3 hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-amber-500 text-white rounded-lg flex items-center justify-center font-bold mr-3">B</span>
                    <span className="font-medium text-gray-900">Medium Priority</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-amber-600">{categoryB}</span>
                    {categoryB > 0 && (
                      expandedSections.categoryB ? (
                        <ChevronUpIcon className="h-5 w-5 text-amber-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-amber-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.categoryB && categoryBLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {categoryBLeads.map((lead) => (
              <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-amber-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                </div>
                        <span className="text-xs font-medium text-amber-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
              </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Category C */}
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('categoryC')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-gray-500 text-white rounded-lg flex items-center justify-center font-bold mr-3">C</span>
                    <span className="font-medium text-gray-900">Low Priority</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-gray-600">{categoryC}</span>
                    {categoryC > 0 && (
                      expandedSections.categoryC ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.categoryC && categoryCLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {categoryCLeads.map((lead) => (
              <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-gray-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-gray-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                </div>
                        <span className="text-xs font-medium text-gray-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
              </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Not Categorized */}
              <div className="bg-slate-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('uncategorized')}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-slate-400 text-white rounded-lg flex items-center justify-center font-bold mr-3">—</span>
                    <span className="font-medium text-gray-900">Not Categorized</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-slate-600">{uncategorizedLeads.length}</span>
                    {uncategorizedLeads.length > 0 && (
                      expandedSections.uncategorized ? (
                        <ChevronUpIcon className="h-5 w-5 text-slate-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-slate-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.uncategorized && uncategorizedLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {uncategorizedLeads.map((lead) => (
              <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-slate-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-slate-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                </div>
                        <span className="text-xs font-medium text-slate-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
              </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Lost - Separate Category */}
              <div className="bg-red-50 rounded-lg overflow-hidden border-2 border-red-200">
                <button
                  onClick={() => toggleSection('lostCategory')}
                  className="w-full flex items-center justify-between p-3 hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center font-bold mr-3">❌</span>
                    <span className="font-medium text-gray-900">Lost</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-red-600">{lostLeadsForCategory.length}</span>
                    {lostLeadsForCategory.length > 0 && (
                      expandedSections.lostCategory ? (
                        <ChevronUpIcon className="h-5 w-5 text-red-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-red-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.lostCategory && lostLeadsForCategory.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {lostLeadsForCategory.map((lead) => (
              <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-red-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-red-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                </div>
                        <span className="text-xs font-medium text-red-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
              </Link>
                    ))}
                  </div>
                )}
                {lostLeadsForCategory.length === 0 && (
                  <div className="px-3 pb-3 text-center py-2">
                    <span className="text-sm text-green-600 font-medium">✅ No lost deals</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📈 Key Performance Summary</h3>
            <div className="space-y-3">
              {/* Contacted but Not Scheduled */}
              <div className="bg-red-50 rounded-lg overflow-hidden border-2 border-red-200">
                <button
                  onClick={() => toggleSection('notContacted')}
                  className="w-full flex items-center justify-between p-3 hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="text-gray-700 font-medium">⚠️ Contacted but Not Scheduled</span>
                    <Tooltip content="Leads that have been contacted but no meeting has been scheduled yet. These are leads in 'Contacted' stage that haven't moved to meeting stages." position="right">
                      <InformationCircleIcon className="h-4 w-4 ml-2 text-red-500" />
                    </Tooltip>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-red-600">{notContactedLeads.length}</span>
                    {notContactedLeads.length > 0 && (
                      expandedSections.notContacted ? (
                        <ChevronUpIcon className="h-5 w-5 text-red-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-red-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.notContacted && notContactedLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {notContactedLeads.map((lead) => (
                      <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-red-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-red-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                          {lead.industry && (
                            <span className="text-xs text-gray-400 ml-2 truncate">
                              • {lead.industry}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-red-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
                {notContactedLeads.length === 0 && (
                  <div className="px-3 pb-3 text-center py-2">
                    <span className="text-sm text-green-600 font-medium">✅ All contacted leads have meetings scheduled!</span>
                  </div>
                )}
              </div>

              {/* New Leads */}
              <div className="bg-blue-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('newLeads')}
                  className="w-full flex items-center justify-between p-3 hover:bg-blue-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">New leads this pipeline</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-blue-600">{newLeads.length}</span>
                    {newLeads.length > 0 && (
                      expandedSections.newLeads ? (
                        <ChevronUpIcon className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-blue-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.newLeads && newLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {newLeads.map((lead) => (
                      <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-blue-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-blue-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Meetings */}
              <div className="bg-cyan-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('meetings')}
                  className="w-full flex items-center justify-between p-3 hover:bg-cyan-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">Meetings scheduled/completed</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-cyan-600">{meetingLeads.length}</span>
                    {meetingLeads.length > 0 && (
                      expandedSections.meetings ? (
                        <ChevronUpIcon className="h-5 w-5 text-cyan-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-cyan-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.meetings && meetingLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {meetingLeads.map((lead) => (
                      <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-cyan-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-cyan-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                          <span className="text-xs text-cyan-600 ml-2 px-1.5 py-0.5 bg-cyan-100 rounded">
                            {lead.stage === 'first_meeting' ? '1st' : '2nd'}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-cyan-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* SOWs */}
              <div className="bg-orange-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('sows')}
                  className="w-full flex items-center justify-between p-3 hover:bg-orange-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">SOWs (In Progress + Sent)</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-orange-600">{sowLeads.length}</span>
                    {sowLeads.length > 0 && (
                      expandedSections.sows ? (
                        <ChevronUpIcon className="h-5 w-5 text-orange-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-orange-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.sows && sowLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {sowLeads.map((lead) => (
                      <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-orange-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-orange-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                          <span className="text-xs text-orange-600 ml-2 px-1.5 py-0.5 bg-orange-100 rounded">
                            {lead.stage === 'sow_in_progress' ? 'In Progress' : 'Sent'}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-orange-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
        </div>

              {/* Negotiation */}
              <div className="bg-indigo-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('negotiation')}
                  className="w-full flex items-center justify-between p-3 hover:bg-indigo-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">In Negotiation</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-indigo-600">{negotiationLeads.length}</span>
                    {negotiationLeads.length > 0 && (
                      expandedSections.negotiation ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.negotiation && negotiationLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {negotiationLeads.map((lead) => (
                      <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-indigo-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-indigo-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Decision Stage */}
              <div className="bg-violet-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('decision')}
                  className="w-full flex items-center justify-between p-3 hover:bg-violet-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">Decision Stage</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl font-bold text-violet-600">{decisionLeads.length}</span>
                    {decisionLeads.length > 0 && (
                      expandedSections.decision ? (
                        <ChevronUpIcon className="h-5 w-5 text-violet-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-violet-600" />
                      )
                    )}
                  </div>
                </button>
                {expandedSections.decision && decisionLeads.length > 0 && (
                  <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                    {decisionLeads.map((lead) => (
              <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="flex items-center justify-between p-2 bg-white rounded hover:bg-violet-100 transition-colors group"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <BuildingOfficeIcon className="h-4 w-4 text-violet-600 mr-2 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {lead.brand || lead.name}
                          </span>
                          {getContactName(lead) && (
                            <span className="text-xs text-gray-500 ml-2 truncate">
                              • {getContactName(lead)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-violet-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View →
                        </span>
              </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Service Line Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🔧 Service Line Breakdown</h3>
          <p className="text-sm text-gray-500 mb-4">View clients by service line to understand which services are in demand</p>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {serviceLineEntries.length > 0 ? (
              serviceLineEntries.map(([serviceLine, serviceLeads]) => {
                const sectionId = `serviceLine_${serviceLine.replace(/\s+/g, '_')}`;
                const isExpanded = expandedSections[sectionId] || false;
                const serviceLineColor = serviceLine === 'Not Specified' ? 'slate' : 
                  serviceLine.toLowerCase().includes('design') ? 'purple' :
                  serviceLine.toLowerCase().includes('development') ? 'blue' :
                  serviceLine.toLowerCase().includes('marketing') ? 'pink' :
                  serviceLine.toLowerCase().includes('consulting') ? 'indigo' :
                  serviceLine.toLowerCase().includes('strategy') ? 'violet' : 'teal';
                
                const colorClasses = {
                  slate: { bg: 'bg-slate-50', hover: 'hover:bg-slate-100', text: 'text-slate-600', icon: 'text-slate-600', border: 'border-slate-200' },
                  purple: { bg: 'bg-purple-50', hover: 'hover:bg-purple-100', text: 'text-purple-600', icon: 'text-purple-600', border: 'border-purple-200' },
                  blue: { bg: 'bg-blue-50', hover: 'hover:bg-blue-100', text: 'text-blue-600', icon: 'text-blue-600', border: 'border-blue-200' },
                  pink: { bg: 'bg-pink-50', hover: 'hover:bg-pink-100', text: 'text-pink-600', icon: 'text-pink-600', border: 'border-pink-200' },
                  indigo: { bg: 'bg-indigo-50', hover: 'hover:bg-indigo-100', text: 'text-indigo-600', icon: 'text-indigo-600', border: 'border-indigo-200' },
                  violet: { bg: 'bg-violet-50', hover: 'hover:bg-violet-100', text: 'text-violet-600', icon: 'text-violet-600', border: 'border-violet-200' },
                  teal: { bg: 'bg-teal-50', hover: 'hover:bg-teal-100', text: 'text-teal-600', icon: 'text-teal-600', border: 'border-teal-200' },
                };
                
                const colors = colorClasses[serviceLineColor as keyof typeof colorClasses] || colorClasses.teal;
                
                return (
                  <div key={serviceLine} className={`${colors.bg} rounded-lg overflow-hidden border ${colors.border}`}>
                    <button
                      onClick={() => toggleSection(sectionId)}
                      className={`w-full flex items-center justify-between p-3 ${colors.hover} transition-colors`}
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <DocumentTextIcon className={`h-5 w-5 ${colors.icon} mr-3 flex-shrink-0`} />
                        <span className="font-medium text-gray-900 truncate">{serviceLine}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-xl font-bold ${colors.text}`}>{serviceLeads.length}</span>
                        {serviceLeads.length > 0 && (
                          isExpanded ? (
                            <ChevronUpIcon className={`h-5 w-5 ${colors.text}`} />
                          ) : (
                            <ChevronDownIcon className={`h-5 w-5 ${colors.text}`} />
                          )
                        )}
                      </div>
                    </button>
                    {isExpanded && serviceLeads.length > 0 && (
                      <div className="px-3 pb-3 space-y-1 max-h-60 overflow-y-auto">
                        {serviceLeads.map((lead) => (
                          <Link
                            key={lead.id}
                            to={`/leads/${lead.id}`}
                            className={`flex items-center justify-between p-2 bg-white rounded ${colors.hover} transition-colors group`}
                          >
                            <div className="flex items-center flex-1 min-w-0">
                              <BuildingOfficeIcon className={`h-4 w-4 ${colors.icon} mr-2 flex-shrink-0`} />
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {lead.brand || lead.name}
                              </span>
                              {lead.lead_name && (
                                <span className="text-xs text-gray-500 ml-2 truncate">
                                  • {lead.lead_name}
                                </span>
                              )}
                              {lead.industry && (
                                <span className="text-xs text-gray-400 ml-2 truncate">
                                  • {lead.industry}
                                </span>
                              )}
                            </div>
                            <span className={`text-xs font-medium ${colors.text} ml-2 opacity-0 group-hover:opacity-100 transition-opacity`}>
                              View →
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">No service lines specified yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Recent Leads</h2>
              <p className="text-sm text-gray-500">Latest 10 leads - click to view details</p>
            </div>
            <Link to="/leads" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
              View All →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Brand / Company</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stage</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {periodFilteredLeads
                  .sort((a, b) => {
                    // Sort by creation date - most recent first
                    const dateA = a.date_created || a.created_at;
                    const dateB = b.date_created || b.created_at;
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1; // Put null dates at end
                    if (!dateB) return -1;
                    return new Date(dateB).getTime() - new Date(dateA).getTime();
                  })
                  .slice(0, 10)
                  .map((lead) => {
                  const stageInfo = STAGES.find(s => s.id === lead.stage) || STAGES[0];
                  const createdDate = lead.date_created || lead.created_at;
                  return (
                    <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/leads/${lead.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{lead.brand || lead.name}</div>
                        {lead.industry && <div className="text-xs text-gray-500">{lead.industry}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {getContactName(lead) || (lead.brand && lead.name !== lead.brand ? lead.name : '—')}
                      </td>
                      <td className="px-4 py-3">
                        <Tooltip content={stageInfo.tooltip} position="top">
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${stageInfo.color} text-white`}>
                            {stageInfo.icon} {stageInfo.name}
                        </span>
                        </Tooltip>
                      </td>
                      <td className="px-4 py-3">
                        {lead.category ? (
                          <Tooltip content={`Category ${lead.category}: ${lead.category === 'A' ? 'High priority' : lead.category === 'B' ? 'Medium priority' : 'Low priority'}`} position="top">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded font-bold text-xs text-white ${
                              lead.category === 'A' ? 'bg-emerald-500' : 
                              lead.category === 'B' ? 'bg-amber-500' : 'bg-gray-500'
                            }`}>
                              {lead.category}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(parseLeadValue(lead.value), 'INR', { compact: true })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {createdDate ? new Date(createdDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {periodFilteredLeads.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      {period === 'all' ? 'No leads found.' : `No leads found for ${period === 'weekly' ? 'this week' : period === 'monthly' ? 'this month' : 'this quarter'}.`} <Link to="/leads" className="text-indigo-600 hover:underline">Create a new lead</Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Insights Modal */}
      {insightsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setInsightsModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {insightsModal.type === 'pipeline' && 'Pipeline Value Insights'}
                {insightsModal.type === 'forecast' && 'Weighted Forecast Insights'}
                {insightsModal.type === 'won' && 'Total Won Value Insights'}
              </h2>
              <button
                onClick={() => setInsightsModal(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              {(() => {
                // Get period-filtered leads for the insight type (matching the main dashboard)
                const filteredLeads = insightsModal.type === 'won' 
                  ? periodFilteredLeads.filter(l => l.stage === 'won')
                  : periodFilteredLeads.filter(l => !['won', 'lost'].includes(l.stage));
                
                // Calculate total value from period-filtered leads (matching the main dashboard)
                const totalValue = filteredLeads.reduce((sum, l) => {
                  const value = parseLeadValue(l.value);
                  if (insightsModal.type === 'forecast') {
                    const prob = parseLeadProbability(l.probability);
                    return sum + (value * prob / 100);
                  }
                  return sum + value;
                }, 0);

                const periodLabel = period === 'all' ? 'all time' : period === 'weekly' ? 'this week' : period === 'monthly' ? 'this month' : 'this quarter';
                
                return (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                      <div className="text-sm text-gray-600 mb-2">
                        {insightsModal.type === 'won' ? 'Total Won Value' : insightsModal.type === 'forecast' ? 'Weighted Forecast' : 'Pipeline Value'} 
                        {period !== 'all' && <span className="ml-2 text-xs">({periodLabel})</span>}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {formatCurrency(totalValue, 'INR', { compact: true })}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {filteredLeads.length} {insightsModal.type === 'won' ? 'deals closed' : 'active deals'} {period !== 'all' && periodLabel}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Deals {period !== 'all' && `(${periodLabel})`}</h3>
                      <div className="space-y-3">
                        {filteredLeads
                          .sort((a, b) => {
                            const aValue = parseLeadValue(a.value);
                            const bValue = parseLeadValue(b.value);
                            if (insightsModal.type === 'forecast') {
                              const aProb = parseLeadProbability(a.probability);
                              const bProb = parseLeadProbability(b.probability);
                              return (bValue * bProb / 100) - (aValue * aProb / 100);
                            }
                            return bValue - aValue;
                          })
                          .slice(0, 10)
                          .map((lead, index) => {
                            const leadValue = parseLeadValue(lead.value);
                            const displayValue = insightsModal.type === 'forecast' 
                              ? leadValue * (parseLeadProbability(lead.probability) / 100)
                              : leadValue;
                            
                            return (
                              <Link
                                key={lead.id}
                                to={`/leads/${lead.id}`}
                                className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                              >
                                <div className="flex items-center flex-1 min-w-0">
                                  <span className="text-sm font-bold text-gray-400 mr-3">#{index + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-gray-900 truncate">
                                      {lead.brand || lead.name}
                                    </div>
                                    {getContactName(lead) && (
                                      <div className="text-sm text-gray-500 truncate">{getContactName(lead)}</div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right ml-4">
                                  <div className="font-bold text-gray-900">
                                    {formatCurrency(displayValue, 'INR', { compact: true })}
                                  </div>
                                  {insightsModal.type === 'forecast' && (
                                    <div className="text-xs text-gray-500">{lead.probability}% probability</div>
                                  )}
                                </div>
                              </Link>
                            );
                          })}
                        {filteredLeads.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            {period === 'all' 
                              ? `No ${insightsModal.type === 'won' ? 'won deals' : 'active deals'} found.`
                              : `No ${insightsModal.type === 'won' ? 'won deals' : 'active deals'} found for ${periodLabel}.`
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </PremiumLayout>
  );
};

export default Dashboard;
